/**
 * @module services/catalogs/openLibraryService
 * @description Service for searching Open Library catalog via their public API
 * @see https://openlibrary.org/developers/api
 */

import type { ItemData, Creator } from '@types';
import type { BaseCatalogRecord, BaseCatalogSearchResponse } from './types';
import { mapLanguageToFullName, capitalizePublisher } from './utils';

// =============================================================================
// TYPES
// =============================================================================

/** Open Library search index options */
export type OpenLibrarySearchIndex =
  | 'q' // All fields (general query)
  | 'title' // Title
  | 'author' // Author name
  | 'isbn' // ISBN
  | 'subject' // Subject
  | 'publisher' // Publisher
  | 'person' // Person as subject
  | 'place'; // Place as subject

/** Open Library sort options */
export type OpenLibrarySort =
  | 'relevance' // Default relevance
  | 'new' // Newest first
  | 'old' // Oldest first
  | 'rating' // By rating
  | 'editions' // By number of editions
  | 'title'; // Alphabetical by title

/** Open Library search result */
export interface OpenLibraryRecord extends BaseCatalogRecord {
  /** Open Library work key (e.g., "OL27448W") */
  olid: string;
  /** Cover image URL (medium size) */
  coverUrl?: string;
}

/** Open Library search response */
export type OpenLibrarySearchResponse = BaseCatalogSearchResponse<OpenLibraryRecord>;

/** Search options */
export interface OpenLibrarySearchOptions {
  /** Search index to use */
  index?: OpenLibrarySearchIndex;
  /** Sort order */
  sort?: OpenLibrarySort;
  /** Page number (1-based) */
  page?: number;
  /** Results per page (max 100) */
  limit?: number;
  /** Preferred language (ISO 639-1) */
  lang?: string;
}

// =============================================================================
// INTERNAL TYPES
// =============================================================================

interface OpenLibraryDoc {
  key: string;
  title?: string;
  subtitle?: string;
  author_name?: string[];
  author_key?: string[];
  first_publish_year?: number;
  publish_year?: number[];
  publisher?: string[];
  language?: string[];
  isbn?: string[];
  subject?: string[];
  cover_i?: number;
  edition_count?: number;
  has_fulltext?: boolean;
  public_scan_b?: boolean;
  number_of_pages_median?: number;
  lccn?: string[];
  oclc?: string[];
  ia?: string[];
  ebook_access?: string;
}

interface OpenLibraryApiResponse {
  numFound: number;
  start: number;
  numFoundExact: boolean;
  docs: OpenLibraryDoc[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const BASE_URL = 'https://openlibrary.org';
const SEARCH_URL = `${BASE_URL}/search.json`;
const COVERS_URL = 'https://covers.openlibrary.org';

const DEFAULT_LIMIT = 10;
const MAX_LIMIT = 100;

const FETCH_FIELDS = [
  'key',
  'title',
  'subtitle',
  'author_name',
  'author_key',
  'first_publish_year',
  'publisher',
  'language',
  'isbn',
  'subject',
  'cover_i',
  'edition_count',
  'number_of_pages_median',
  'lccn',
  'oclc',
].join(',');

// =============================================================================
// SERVICE
// =============================================================================

class OpenLibraryService {
  /**
   * Search Open Library catalog
   */
  async search(
    query: string,
    options: OpenLibrarySearchOptions = {}
  ): Promise<OpenLibrarySearchResponse> {
    const { index = 'q', sort, page = 1, limit = DEFAULT_LIMIT, lang } = options;

    if (!query.trim()) {
      return { totalResults: 0, records: [] };
    }

    // Build search URL
    const params = new URLSearchParams();

    if (index === 'q') {
      params.set('q', query);
    } else {
      params.set(index, query);
    }

    params.set('page', String(page));
    params.set('limit', String(Math.min(limit, MAX_LIMIT)));
    params.set('fields', FETCH_FIELDS);

    if (sort && sort !== 'relevance') {
      params.set('sort', sort);
    }

    if (lang) {
      params.set('lang', lang);
    }

    const url = `${SEARCH_URL}?${params.toString()}`;
    return this.executeSearch(url);
  }

  /**
   * Search by ISBN
   */
  async searchByISBN(isbn: string): Promise<OpenLibraryRecord | null> {
    const cleanIsbn = isbn.replace(/[-\s]/g, '');
    const result = await this.search(cleanIsbn, { index: 'isbn', limit: 1 });
    return result.records[0] || null;
  }

  /**
   * Execute the search request
   */
  private async executeSearch(url: string): Promise<OpenLibrarySearchResponse> {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Open Library API error: ${response.status}`);
    }

    const data: OpenLibraryApiResponse = await response.json();

    return {
      totalResults: data.numFound,
      records: data.docs
        .map((doc) => this.parseDocument(doc))
        .filter((r): r is OpenLibraryRecord => r !== null),
    };
  }

  /**
   * Parse an Open Library document into our record format
   */
  private parseDocument(doc: OpenLibraryDoc): OpenLibraryRecord | null {
    if (!doc.key) {
      return null;
    }

    const olid = doc.key.replace('/works/', '');

    // Build creators array
    const creators: Creator[] = [];
    if (doc.author_name && doc.author_name.length > 0) {
      for (const authorName of doc.author_name) {
        const parts = authorName.split(' ');
        if (parts.length > 1) {
          creators.push({
            creatorType: 'author',
            firstName: parts.slice(0, -1).join(' '),
            lastName: parts[parts.length - 1],
          });
        } else {
          creators.push({
            creatorType: 'author',
            name: authorName,
          });
        }
      }
    }

    // Build title
    let title = doc.title || '';
    if (doc.subtitle) {
      title = `${title}: ${doc.subtitle}`;
    }

    // Build the item
    const item: Partial<ItemData> = {
      itemType: 'book',
      title,
      creators,
    };

    if (doc.first_publish_year) {
      item.date = String(doc.first_publish_year);
    }

    if (doc.publisher && doc.publisher.length > 0) {
      item.publisher = capitalizePublisher(doc.publisher[0]);
    }

    if (doc.language && doc.language.length > 0) {
      item.language = mapLanguageToFullName(doc.language[0]);
    }

    if (doc.isbn && doc.isbn.length > 0) {
      const isbn13 = doc.isbn.find((i) => i.length === 13);
      const isbn10 = doc.isbn.find((i) => i.length === 10);
      item.ISBN = isbn13 || isbn10 || doc.isbn[0];
    }

    if (doc.number_of_pages_median) {
      item.numPages = String(doc.number_of_pages_median);
    }

    if (doc.lccn && doc.lccn.length > 0) {
      item.callNumber = doc.lccn[0];
    }

    if (doc.subject && doc.subject.length > 0) {
      const subjects = doc.subject.slice(0, 5).join(', ');
      item.extra = `Subjects: ${subjects}`;
    }

    if (doc.edition_count) {
      const existingExtra = item.extra || '';
      item.extra = existingExtra
        ? `${existingExtra}\nEditions: ${doc.edition_count}`
        : `Editions: ${doc.edition_count}`;
    }

    // Cover URL
    let coverUrl: string | undefined;
    if (doc.cover_i) {
      coverUrl = `${COVERS_URL}/b/id/${doc.cover_i}-M.jpg`;
    }

    // Catalog URL
    const catalogUrl = `${BASE_URL}/works/${olid}`;

    return {
      id: olid,
      olid,
      item,
      coverUrl,
      catalogUrl,
    };
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const openLibraryService = new OpenLibraryService();
export default openLibraryService;
